import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ChatListRow from '../components/ChatListRow'
import { GenieChat, deleteGenieChat, getAllGenieChats } from '../api/client'
import { useNotify } from '../components/notice/useNotify'
import Spinner from '../components/Spinner'
import ConfirmationModal from '../components/ConfirmationModal'

// Stage 5 wires the advice index to the unified `genie_chats` store:
// `GET /api/genie/chats?kind=advice` returns the same shape the Genie
// history page already consumes, scoped to advice-kind rows. Stage 6
// will surface triggered-advice and suggestion cards on the project page;
// this page is the cross-project history index.

const AdvicePage: React.FC = () => {
    const notify = useNotify()
    const navigate = useNavigate()
    const [chats, setChats] = useState<GenieChat[]>([])
    const [loading, setLoading] = useState(true)
    const [pendingDelete, setPendingDelete] = useState<number | null>(null)

    const fetchChats = async () => {
        setLoading(true)
        try {
            const response = await getAllGenieChats('advice')
            setChats(response.chats)
        } catch (e) {
            console.error(e)
            notify.error('Failed to load advice chats')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchChats()
    }, [])

    const confirmDelete = async () => {
        if (pendingDelete == null) return
        try {
            await deleteGenieChat(pendingDelete)
            setPendingDelete(null)
            await fetchChats()
        } catch (e) {
            console.error(e)
            notify.error('Failed to delete chat')
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col min-h-screen bg-blush-light">
                <div className="container mx-auto pb-8 sm:pt-8 flex justify-center items-center flex-grow">
                    <Spinner />
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col min-h-screen bg-blush-light">
            <div className="container mx-auto px-4 pb-8 sm:pt-8">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-800">Advice</h1>
                    <p className="text-gray-600 mt-2">
                        Conversations from your project squad.
                    </p>
                </div>

                {chats.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No advice conversations yet.</p>
                        <p className="text-sm mt-2">
                            New advice chats are surfaced when your projects
                            accumulate fresh activity.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {chats.map((chat) => (
                            <ChatListRow
                                key={chat.id}
                                title={chat.title || `Advice Chat #${chat.id}`}
                                meta={
                                    <>
                                        Started{' '}
                                        {new Date(
                                            chat.created_at,
                                        ).toLocaleDateString()}
                                        {chat.project_id && (
                                            <>
                                                <span className="mx-2 text-gray-300">
                                                    •
                                                </span>
                                                <span className="text-amber-800 font-medium">
                                                    {chat.project_emoji && (
                                                        <span className="mr-1">
                                                            {chat.project_emoji}
                                                        </span>
                                                    )}
                                                    {chat.project_title}
                                                </span>
                                            </>
                                        )}
                                    </>
                                }
                                onContinue={() => navigate(`/advice/${chat.id}`)}
                                onDelete={(e) => {
                                    e.stopPropagation()
                                    setPendingDelete(chat.id)
                                }}
                            />
                        ))}
                    </div>
                )}

                {pendingDelete != null && (
                    <ConfirmationModal
                        isOpen={pendingDelete != null}
                        onClose={() => setPendingDelete(null)}
                        onConfirm={confirmDelete}
                        title="Delete Chat"
                        message="Are you sure you want to delete this chat? This action cannot be undone."
                        confirmText="Delete"
                        cancelText="Cancel"
                    />
                )}
            </div>
        </div>
    )
}

export default AdvicePage
